import React from 'react';

// Clean up enum-style values like "KeywordMatchType.EXACT" → "Exact".
// IMPORTANT: must NOT touch numeric strings (e.g. TR-locale "₺43.163.149", "25.91x", "0.31")
// — only Pascal/UPPER prefix . UPPER suffix patterns count as enums.
const ENUM_PATTERN = /^[A-Z][A-Za-z0-9]*\.[A-Z][A-Z0-9_]*$/;
const cleanEnumValue = (val) => {
  if (typeof val !== 'string') return val;
  if (!ENUM_PATTERN.test(val)) return val; // anything that isn't strictly enum-shaped is left alone
  const parts = val.split('.');
  const last = parts[parts.length - 1];
  return last.charAt(0).toUpperCase() + last.slice(1).toLowerCase();
};

export const formatValue = (value, format, key) => {
  if (value == null || value === '') return '—';

  switch (format) {
    case 'currency': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      const color = /cost|spend|harcama|cpc/i.test(key) ? 'text-red-600' : 'text-green-600';
      return <span className={`${color} font-semibold`}>₺{Math.round(num).toLocaleString('tr-TR')}</span>;
    }
    case 'number': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      return num.toLocaleString('tr-TR');
    }
    case 'percent': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      return <span className="text-purple-600 font-medium">{num.toFixed(2)}%</span>;
    }
    case 'multiplier': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      return <span className="text-purple-700 font-bold">{num.toFixed(2)}x</span>;
    }
    case 'change': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      const color = num >= 0 ? 'text-green-600' : 'text-red-600';
      const prefix = num >= 0 ? '+' : '';
      return <span className={`${color} font-medium`}>{prefix}{num.toFixed(1)}%</span>;
    }
    case 'decimal': {
      const num = Number(value);
      return isNaN(num) ? String(value) : num.toFixed(1);
    }
    case 'date': {
      const str = String(value);
      if (str.length >= 10) return str.slice(0, 10);
      return str;
    }
    default:
      return cleanEnumValue(String(value));
  }
};

const DataTableCard = ({ card }) => {
  if (!card || !card.columns || !card.rows) return null;
  const rows = card.rows.slice(0, 20);

  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-gray-700">{card.title}</div>
        {card.period && (
          <div className="text-[10px] text-gray-400 font-mono">
            {card.period.start} → {card.period.end}
          </div>
        )}
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="w-full text-[12px] text-gray-800">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {card.columns.map((col, i) => (
                <th
                  key={i}
                  className={`px-3 py-2 font-semibold text-gray-500 uppercase tracking-wider text-[10px] ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr
                key={ri}
                className={`border-b border-gray-50 last:border-0 hover:bg-blue-50/30 transition-colors ${ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
              >
                {card.columns.map((col, ci) => (
                  <td
                    key={ci}
                    className={`px-3 py-1.5 ${col.align === 'right' ? 'text-right' : 'text-left'} ${ci === 0 ? 'font-medium text-gray-900' : ''}`}
                  >
                    {formatValue(row[col.key], col.format, col.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length >= 20 && (
        <div className="mt-1 text-[10px] text-gray-400 text-center">
          Showing top 20 results
        </div>
      )}
    </div>
  );
};

export default DataTableCard;
